import React, { useState } from 'react';
import { CaretUpDown, Check, MagnifyingGlass } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

// Every typed word must appear somewhere in name / code / SKU / category.
const matchAllWords = (value, search) => {
  const hay = value.toLowerCase();
  return search.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w)) ? 1 : 0;
};

// Searchable product dropdown — matches name, product code, SKU and category.
export const ProductPicker = ({ products = [], value, onChange, placeholder = 'Select product…', noneLabel, showPrice = true, testid = 'product-picker' }) => {
  const [open, setOpen] = useState(false);
  const selected = products.find((p) => p.id === value);

  const pick = (id) => { onChange(id); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full flex items-center justify-between gap-2 px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white text-left focus:ring-2 focus:ring-[#00bfb3] outline-none hover:border-[#00a69c] transition-colors"
          data-testid={testid}
        >
          <span className={`truncate ${selected ? 'text-[#052A23]' : 'text-[#6D8E87]'}`}>
            {selected ? `${selected.name}${showPrice ? ` - ${inr(selected.price)}` : ''}` : (noneLabel || placeholder)}
          </span>
          <CaretUpDown size={14} className="shrink-0 text-[#6D8E87]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[320px]" align="start">
        <Command filter={matchAllWords}>
          <CommandInput placeholder="Search name, code, SKU or category…" data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>
              <span className="inline-flex items-center gap-1.5 text-[#6D8E87]"><MagnifyingGlass size={14} /> No products match</span>
            </CommandEmpty>
            <CommandGroup>
              {noneLabel && (
                <CommandItem value="__none__ none general" onSelect={() => pick('')} className="cursor-pointer data-[selected=true]:bg-[#E8F5F3] aria-selected:bg-[#E8F5F3]" data-testid={`${testid}-option-none`}>
                  <span className="text-[#6D8E87]">{noneLabel}</span>
                </CommandItem>
              )}
              {products.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.name} ${p.product_code || ''} ${p.sku_code || ''} ${p.category || ''} ${p.id}`}
                  onSelect={() => pick(p.id)}
                  className="flex items-center gap-2 cursor-pointer data-[selected=true]:bg-[#E8F5F3] data-[selected=true]:text-[#052A23] aria-selected:bg-[#E8F5F3] aria-selected:text-[#052A23]"
                  data-testid={`${testid}-option-${p.id}`}
                >
                  <Check size={14} className={p.id === value ? 'text-[#00a69c]' : 'opacity-0'} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[#052A23]">{p.name}</div>
                    <div className="truncate text-[11px] text-[#6D8E87]">
                      {[p.product_code, p.sku_code, p.category].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  {showPrice && <span className="shrink-0 text-xs font-mono text-[#3B5A54]">{inr(p.price)}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
